import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';
import { markSignedIn, markSignedOut } from '../lib/sessionMarker.js';
import { clearOpenItemsShown } from '../api/openItems.js';
import { AuthContext, type AuthUser } from './useAuth.js';

type MeResponse = { user: AuthUser };

const ME_KEY = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<AuthUser | null, Error>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const res = await api<MeResponse>('/api/auth/me');
        return res.user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          return null;
        }
        throw e;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  /**
   * כניסה.
   *
   * מחזירה `'2fa'` כשהסיסמה התקבלה והחשבון דורש קוד. הסשן **אינו** פתוח
   * בשלב הזה — השרת מחזיק `pending2fa` בלבד, ולכן אין מצב ביניים שאפשר
   * להשתמש בו.
   */
  const login = useCallback(
    async (username: string, password: string): Promise<'ok' | '2fa'> => {
      const res = await api<MeResponse & { twoFactorRequired?: boolean }>('/api/auth/login', {
        method: 'POST',
        body: { username, password },
      });
      if (res.twoFactorRequired) return '2fa';
      queryClient.setQueryData(ME_KEY, res.user);
      return 'ok';
    },
    [queryClient],
  );

  /** השלמת כניסה שנעצרה לקוד. */
  const completeTwoFactor = useCallback(
    async (code: string) => {
      const res = await api<MeResponse>('/api/auth/login/2fa', { method: 'POST', body: { code } });
      queryClient.setQueryData(ME_KEY, res.user);
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api<{ ok: true }>('/api/auth/logout', { method: 'POST' });
    } finally {
      markSignedOut();
      clearOpenItemsShown(); // הכניסה הבאה מראה שוב את מה שפתוח
      queryClient.setQueryData(ME_KEY, null);
      queryClient.clear();
    }
  }, [queryClient]);

  // ── גישה מלאה = מנכ״ל, בכל המסכים (אלי 05/10: „ביקשתי לפתוח למדינה את כל
  // ההרשאות") ── נמדד: למדינה FULL_ACCESS ועוד 13 הרשאות, ובעץ הפרויקטים אין לה
  // אפשרות לשנות — 61 בדיקות במסכים שואלות `role === 'CEO'` ישירות ולא את
  // היכולת. נקודה אחת במקום 61: התפקיד האפקטיבי הוא CEO, והתפקיד האמיתי נשמר
  // ב-`baseRole` (לתצוגה). השרת עדיין השומר — `requireRole` מכבד FULL_ACCESS.
  // „היה כאן סשן" — כדי שמסך הכניסה ידע לומר „ההתחברות פגה" (UX X1).
  useEffect(() => { if (data) markSignedIn(); }, [data]);

  const user = useMemo(() => {
    const u = data ?? null;
    if (!u || u.role === 'CEO' || !u.capabilities?.includes('FULL_ACCESS')) return u;
    return { ...u, role: 'CEO' as AuthUser['role'], baseRole: u.role };
  }, [data]);
  const can = useCallback((capability: string) => !!user?.capabilities?.includes(capability), [user]);

  const value = {
    user,
    isLoading,
    error: error ?? null,
    login,
    completeTwoFactor,
    logout,
    can,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
